import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import {
  AlertIcon,
  AlertOffIcon,
  ExternalLinkIcon,
  PinFilledIcon,
  PinOutlinedIcon,
  SpeakerIcon,
  SpeakerOffIcon,
} from '@sharedCR/coreDesignSystem/icons';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import { MessagesPaths } from 'app/configs/routesConfig/messages';
import {
  buildTeamChatMessagePermalink,
  getApplicationIdFromChannelId,
  getChannelDisplayName,
  getChannelId,
  getChannelLastMessage,
  getChannelUnreadCount,
  getMessageText,
  getMessageTimestamp,
  isChannelPinned,
} from 'app/shared/components/TeamChat/TeamChat.utils';
import TextFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/TextFieldCDS';
import SecondaryTabGroupCDS from 'app/shared/componentsRestyled/coreDesignSystem/SecondaryTabGroupCDS';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { LoanApplicationTabRouteParam } from 'constants/loanApplication';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getUuid } from 'store/auth/selectors';
import {
  fetchChannelsAction,
  fetchUserMentionsAction,
  pinChatAction,
  unpinChatAction,
} from 'store/teamChat/actions';
import {
  makeSelectTeamChatUserMentionsWithChannels,
  selectTeamChatAllChannels,
  selectTeamChatIsLoadingChannels,
  selectTeamChatIsLoadingUserMentions,
} from 'store/teamChat/selectors';

import HeaderChatChannelInboxRow from '../HeaderChatChannelInboxRow';
import HeaderChatMentionsList from '../HeaderChatMentionsList';
import {
  formatMentionTimestamp,
  truncatePreview,
} from '../HeaderChatMentionsList/HeaderChatMentionsList.utils';

import HeaderChatActiveChannelView from './components/HeaderChatActiveChannelView';
import HeaderChatLoadingSkeleton from './components/HeaderChatLoadingSkeleton';
import {
  HEADER_CHAT_INBOX_EMPTY_LABEL,
  HEADER_CHAT_MENTIONS_EMPTY_LABEL,
  HEADER_CHAT_SEARCH_EMPTY_LABEL_PREFIX,
  HEADER_CHAT_SEARCH_PLACEHOLDER,
  HEADER_CHAT_TAB_OPTIONS,
  HEADER_CHAT_VIEW_ALL_LABEL,
  HeaderChatFilterOption,
} from './HeaderChatPopover.constants';
import {
  bodyStyles,
  emptyStyles,
  emptySubtitleStyles,
  emptyTitleStyles,
  headerActionsStyles,
  headerStyles,
  headerTitleStyles,
  notificationsButtonActiveStyles,
  notificationsButtonStyles,
  rootStyles,
  rowActionButtonActiveStyles,
  rowActionButtonStyles,
  searchInputStyles,
  sectionHeaderStyles,
  tabsRowStyles,
  toolbarStyles,
  viewAllButtonStyles,
  viewAllRowStyles,
} from './HeaderChatPopover.styles';
import { HeaderChatPopoverTestIds } from './HeaderChatPopover.testIds';
import { filterChannelsForPopover, groupChannelsForPopover } from './HeaderChatPopover.utils';
import useTeamChatBrowserNotifications from './hooks/useTeamChatBrowserNotifications';
import useTeamChatSoundNotifications from './hooks/useTeamChatSoundNotifications';

const HeaderChatPopover = memo(({ initialChannelId = null, onClose = null }) => {
  const history = useHistory();

  const dispatch = useDispatch();

  const selectMentions = useMemo(makeSelectTeamChatUserMentionsWithChannels, []);

  const loggedUserId = useSelector(getUuid);
  const channels = useSelector(selectTeamChatAllChannels);
  const isLoadingChannels = useSelector(selectTeamChatIsLoadingChannels);
  const mentions = useSelector(selectMentions);
  const isLoadingMentions = useSelector(selectTeamChatIsLoadingUserMentions);

  const {
    isSupported: areNotificationsSupported,
    isEnabled: areNotificationsEnabled,
    onToggleEnabled: onToggleNotifications,
  } = useTeamChatBrowserNotifications();

  const {
    isSupported: isSoundSupported,
    isEnabled: isSoundEnabled,
    onToggleEnabled: onToggleSound,
  } = useTeamChatSoundNotifications();

  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState(HeaderChatFilterOption.ALL);
  const [activeChannelId, setActiveChannelId] = useState(initialChannelId);

  const channelPinnedLookup = useMemo(() => {
    const lookup = {};

    channels.forEach(channel => {
      const chatId = getChannelId(channel);

      if (chatId) {
        lookup[chatId] = isChannelPinned(channel);
      }
    });

    return lookup;
  }, [channels]);

  const filteredChannels = useMemo(
    () => filterChannelsForPopover({ channels, searchQuery }),
    [channels, searchQuery],
  );

  const sections = useMemo(
    () => groupChannelsForPopover({ channels: filteredChannels, mentions }),
    [filteredChannels, mentions],
  );

  const activeChannel = useMemo(
    () => channels.find(channel => getChannelId(channel) === activeChannelId) ?? null,
    [channels, activeChannelId],
  );

  const onSearchChange = useCallback(e => {
    setSearchQuery(e.target.value);
  }, []);

  const onTabChange = useCallback(nextValue => {
    setFilter(nextValue);
  }, []);

  const onSelectChannel = useCallback(({ chatId }) => {
    setActiveChannelId(chatId ?? null);
  }, []);

  const onBackToList = useCallback(() => {
    setActiveChannelId(null);
  }, []);

  const onPinClick = useCallback(
    e => {
      e.stopPropagation();

      const { chatId } = e.currentTarget.dataset;

      if (!chatId) {
        return;
      }

      if (channelPinnedLookup[chatId]) {
        dispatch(unpinChatAction({ chatId }));

        return;
      }

      dispatch(pinChatAction({ chatId }));
    },
    [dispatch, channelPinnedLookup],
  );

  const onSelectMention = useCallback(({ chatId, messageId }) => {
    if (!chatId) {
      return;
    }

    setActiveChannelId(chatId);

    if (messageId && typeof window !== 'undefined') {
      const permalink = buildTeamChatMessagePermalink({ chatId, messageId });

      if (permalink) {
        const hashIndex = permalink.indexOf('#');

        if (hashIndex !== -1) {
          window.location.hash = permalink.slice(hashIndex + 1);
        }
      }
    }
  }, []);

  const onOpenFullChat = useCallback(() => {
    history.push(MessagesPaths.messages.path);

    if (onClose) {
      onClose();
    }
  }, [history, onClose]);

  const onJumpToLoanClick = useCallback(
    e => {
      e.stopPropagation();

      const { applicationId } = e.currentTarget.dataset;

      if (!applicationId) {
        return;
      }

      const pathname = LoansPaths.applicationManagement.generatePath({
        [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
        [LOANS_ROUTING_PARAMS.tab]: LoanApplicationTabRouteParam.ACTIVITIES,
      });

      history.push(pathname);

      if (onClose) {
        onClose();
      }
    },
    [history, onClose],
  );

  useEffectSafeStrictMode(() => {
    dispatch(fetchChannelsAction());
    dispatch(fetchUserMentionsAction());
  }, [dispatch]);

  const hasChannels = channels.length > 0;
  const hasFilteredChannels = filteredChannels.length > 0;
  const isMentionsFilter = filter === HeaderChatFilterOption.MENTIONS;
  const hasAnyMentions = mentions.length > 0;
  const showLoadingSkeleton = isLoadingChannels && !hasChannels;
  const showEmptyInbox = !isLoadingChannels && !hasChannels;
  const showEmptySearch = !showLoadingSkeleton && hasChannels && !hasFilteredChannels;

  return activeChannelId && activeChannel ? (
    <HeaderChatActiveChannelView
      channel={activeChannel}
      currentUserId={loggedUserId}
      onBack={onBackToList}
      onClose={onClose}
    />
  ) : (
    <Box sx={rootStyles}>
      <Box sx={headerStyles}>
        <Typography sx={headerTitleStyles} variant="bold16">
          Messages
        </Typography>
        <Box sx={headerActionsStyles}>
          {areNotificationsSupported && (
            <Tooltip
              title={
                areNotificationsEnabled
                  ? 'Disable desktop notifications'
                  : 'Enable desktop notifications'
              }
            >
              <IconButton
                aria-label={
                  areNotificationsEnabled
                    ? 'Disable desktop notifications'
                    : 'Enable desktop notifications'
                }
                data-testid={HeaderChatPopoverTestIds.NOTIFICATIONS_TOGGLE}
                size="small"
                sx={
                  areNotificationsEnabled
                    ? notificationsButtonActiveStyles
                    : notificationsButtonStyles
                }
                onClick={onToggleNotifications}
              >
                {areNotificationsEnabled ? (
                  <AlertIcon size={IconSize[20]} />
                ) : (
                  <AlertOffIcon size={IconSize[20]} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {isSoundSupported && (
            <Tooltip title={isSoundEnabled ? 'Mute sounds' : 'Enable sounds'}>
              <IconButton
                aria-label={isSoundEnabled ? 'Mute sounds' : 'Enable sounds'}
                data-testid={HeaderChatPopoverTestIds.SOUND_TOGGLE}
                size="small"
                sx={isSoundEnabled ? notificationsButtonActiveStyles : notificationsButtonStyles}
                onClick={onToggleSound}
              >
                {isSoundEnabled ? (
                  <SpeakerIcon size={IconSize[20]} />
                ) : (
                  <SpeakerOffIcon size={IconSize[20]} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={toolbarStyles}>
        <TextFieldCDS
          dataTestId={HeaderChatPopoverTestIds.SEARCH}
          name="headerChatSearch"
          placeholder={HEADER_CHAT_SEARCH_PLACEHOLDER}
          size="small"
          sx={searchInputStyles}
          value={searchQuery}
          onChange={onSearchChange}
        />
        <Box sx={tabsRowStyles}>
          <SecondaryTabGroupCDS
            buttons={HEADER_CHAT_TAB_OPTIONS}
            value={filter}
            onChange={onTabChange}
          />
        </Box>
      </Box>

      <Box sx={bodyStyles}>
        {isMentionsFilter && isLoadingMentions && !hasAnyMentions && <HeaderChatLoadingSkeleton />}

        {isMentionsFilter && !isLoadingMentions && !hasAnyMentions && (
          <Box sx={emptyStyles}>
            <Typography variant="regular14">{HEADER_CHAT_MENTIONS_EMPTY_LABEL}</Typography>
          </Box>
        )}

        {isMentionsFilter && hasAnyMentions && (
          <HeaderChatMentionsList mentions={mentions} onSelectMention={onSelectMention} />
        )}

        {!isMentionsFilter && showLoadingSkeleton && <HeaderChatLoadingSkeleton />}

        {!isMentionsFilter && showEmptyInbox && (
          <Box sx={emptyStyles}>
            <Typography sx={emptyTitleStyles} variant="medium14">
              No conversations yet
            </Typography>
            <Typography sx={emptySubtitleStyles} variant="regular14">
              {HEADER_CHAT_INBOX_EMPTY_LABEL}
            </Typography>
          </Box>
        )}

        {!isMentionsFilter && showEmptySearch && (
          <Box sx={emptyStyles}>
            <Typography variant="regular14">
              {`${HEADER_CHAT_SEARCH_EMPTY_LABEL_PREFIX}"${searchQuery.trim()}"`}
            </Typography>
          </Box>
        )}

        {!isMentionsFilter &&
          hasFilteredChannels &&
          sections.map(section => (
            <Box key={section.key}>
              <Box sx={sectionHeaderStyles}>
                <Typography variant="medium12">{section.label}</Typography>
              </Box>
              {section.items.map(channel => {
                const chatId = getChannelId(channel);
                const pinned = !!channelPinnedLookup[chatId];
                const loanApplicationId = getApplicationIdFromChannelId(chatId);
                const unreadCount = getChannelUnreadCount(channel);
                const isUnread = unreadCount > 0;
                const lastMessage = getChannelLastMessage(channel);
                const previewText = truncatePreview(getMessageText(lastMessage));
                const lastMessageTimestamp = getMessageTimestamp(lastMessage);
                const displayTimestamp = formatMentionTimestamp(lastMessageTimestamp);

                const actionSlot = (
                  <>
                    {!!loanApplicationId && (
                      <Tooltip title="Open loan">
                        <IconButton
                          aria-label="Open loan editor"
                          data-application-id={loanApplicationId}
                          data-testid={HeaderChatPopoverTestIds.OPEN_LOAN}
                          size="small"
                          sx={rowActionButtonStyles}
                          onClick={onJumpToLoanClick}
                        >
                          <ExternalLinkIcon size={IconSize[20]} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={pinned ? 'Unpin' : 'Pin'}>
                      <IconButton
                        aria-label={pinned ? 'Unpin conversation' : 'Pin conversation'}
                        data-chat-id={chatId}
                        data-testid={HeaderChatPopoverTestIds.PIN}
                        size="small"
                        sx={pinned ? rowActionButtonActiveStyles : rowActionButtonStyles}
                        onClick={onPinClick}
                      >
                        {pinned ? (
                          <PinFilledIcon size={IconSize[20]} />
                        ) : (
                          <PinOutlinedIcon size={IconSize[20]} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </>
                );

                return (
                  <HeaderChatChannelInboxRow
                    actionSlot={actionSlot}
                    channel={channel}
                    channelName={getChannelDisplayName(channel) || 'Conversation'}
                    chatId={chatId}
                    key={chatId}
                    preview={previewText}
                    timestamp={displayTimestamp}
                    unread={isUnread}
                    unreadCount={unreadCount}
                    onSelect={onSelectChannel}
                  />
                );
              })}
            </Box>
          ))}
      </Box>

      {!isMentionsFilter && (hasChannels || hasAnyMentions) && (
        <Box sx={viewAllRowStyles}>
          <Button
            data-testid={HeaderChatPopoverTestIds.VIEW_ALL}
            size="small"
            sx={viewAllButtonStyles}
            variant="text"
            onClick={onOpenFullChat}
          >
            {HEADER_CHAT_VIEW_ALL_LABEL}
          </Button>
        </Box>
      )}
    </Box>
  );
});

HeaderChatPopover.propTypes = {
  initialChannelId: PropTypes.string,
  onClose: PropTypes.func,
};

export default HeaderChatPopover;
